import { Card } from "@/components/ui/card";
import { formatCurrency } from "../../lib/utils";
import { formatOccupancyPercent } from "../../lib/finance/units";
import { useKpiDataV2 } from "@/hooks/useKpiDataV2";
import { Deal } from "@/types/deal";
import { ICurrency } from "@/types";

export default function KpiYearTable({
  yearCount,
  currency,
  deal,
}: {
  dealId: string;
  yearCount: number;
  currency: ICurrency;
  deal: Deal;
}) {
  const {
    years,
    adr,
    occupancy,
    revpar,
    toplineIndex,
    inflationIndex,
    revenueRamp,
    costRamp,
  } = useKpiDataV2(deal, yearCount);

  const fmtPercent = (v: number) => {
    return formatOccupancyPercent(v / 100);
  };

  const fmtMoney = (v: number) => {
    return formatCurrency(v, currency);
  };
  const fmtMultiplier = (v: number, isRampValue = true) => {
    if (isRampValue) {
      const percent = Math.round(v * 100);
      return `${percent}%`;
    } else {
      return v.toFixed(2) + "×";
    }
  };

  return (
    <Card className="mt-6 border bg-muted/20">
      <div className="p-4">
        <div className="text-sm font-semibold mb-3">Key KPIs — All Years</div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground">
                <th className="text-left py-2 pr-3">KPI</th>
                {years.map((y) => (
                  <th
                    key={y}
                    className="text-right py-2 px-3 whitespace-nowrap"
                  >
                    {y === "y0" ? "Pre-Op" : `Year ${y.slice(1)}`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t">
                <td className="py-2 pr-3 font-medium">ADR</td>
                {years.map((y) => (
                  <td key={`adr-${y}`} className="text-right py-2 px-3">
                    {fmtMoney(adr[y])}
                  </td>
                ))}
              </tr>
              <tr className="border-t">
                <td className="py-2 pr-3 font-medium">Occupancy</td>
                {years.map((y) => (
                  <td key={`occ-${y}`} className="text-right py-2 px-3">
                    {fmtPercent(occupancy[y])}
                  </td>
                ))}
              </tr>
              <tr className="border-t">
                <td className="py-2 pr-3 font-medium">RevPAR</td>
                {years.map((y) => (
                  <td key={`revpar-${y}`} className="text-right py-2 px-3">
                    {fmtMoney(revpar[y])}
                  </td>
                ))}
              </tr>
              <tr className="border-t">
                <td className="py-2 pr-3 font-medium">Topline Index</td>
                {years.map((y) => (
                  <td key={`topline-${y}`} className="text-right py-2 px-3">
                    {fmtMultiplier(toplineIndex[y])}
                  </td>
                ))}
              </tr>
              <tr className="border-t">
                <td className="py-2 pr-3 font-medium">Inflation Index</td>
                {years.map((y) => (
                  <td key={`inflation-${y}`} className="text-right py-2 px-3">
                    {fmtMultiplier(inflationIndex[y])}
                  </td>
                ))}
              </tr>
              <tr className="border-t">
                <td className="py-2 pr-3 font-medium">Revenue Ramp</td>
                {years.map((y) => (
                  <td key={`ramp-rev-${y}`} className="text-right py-2 px-3">
                    {fmtMultiplier(revenueRamp[y])}
                  </td>
                ))}
              </tr>
              <tr className="border-t">
                <td className="py-2 pr-3 font-medium">Cost Ramp</td>
                {years.map((y) => (
                  <td key={`ramp-cost-${y}`} className="text-right py-2 px-3">
                    {fmtMultiplier(costRamp[y])}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
          <p className="text-xs text-blue-800">
            <strong>Calculation Logic:</strong>
            <br />• ADR = Base ADR × Revenue Ramp × Topline Index
            <br />• Occupancy = Base Occupancy × Revenue Ramp (max 100%)
            <br />• RevPAR = ADR × (Occupancy/100)
            <br />• Topline Index = (1 + Topline Growth %)^Year
            <br />• Inflation Index = (1 + Inflation %)^Year
          </p>
        </div>
      </div>
    </Card>
  );
}
